import { InlineStack, Tooltip, type BoxProps } from '@customerio/pluma-components/react';
import { ProviderOptionCard, type PushProvider } from '@/features/push-credentials';

const LOCKED_MESSAGE = 'Select "Edit authorization" to change provider.';

const IOS_PROVIDERS: readonly PushProvider[] = ['apns', 'fcm'];

type ProviderChoiceProps = {
  provider: PushProvider;
  isSelected: boolean;
  canSelect: boolean;
  showLockedMessage: boolean;
  onSelect: (provider: PushProvider) => void;
} & BoxProps;

/**
 * A locked card's radio is disabled and so out of the tab order; the card takes
 * focus itself while it has the message to show, so the message can be reached
 * from the keyboard as well as by hovering.
 */
function ProviderChoice({
  provider,
  isSelected,
  canSelect,
  showLockedMessage,
  onSelect,
  ...boxProps
}: ProviderChoiceProps) {
  return (
    <Tooltip isCustom={true} content={LOCKED_MESSAGE} isDisabled={!showLockedMessage}>
      {(context) => (
        <ProviderOptionCard
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps({ tabIndex: showLockedMessage ? 0 : undefined })}
          provider={provider}
          name="provider"
          canSelect={canSelect}
          isSelected={isSelected}
          onCheckedChange={() => onSelect(provider)}
          data-test-provider-option={provider}
          {...boxProps}
        />
      )}
    </Tooltip>
  );
}

export type IosProviderChooserProps = {
  provider: PushProvider;
  canSelect: boolean;
  onSelect: (provider: PushProvider) => void;
};

export function IosProviderChooser({ provider, canSelect, onSelect }: IosProviderChooserProps) {
  return (
    <InlineStack gap="150" alignBlock="stretch">
      {IOS_PROVIDERS.map((option) => (
        <ProviderChoice
          key={option}
          provider={option}
          isSelected={provider === option}
          canSelect={canSelect}
          showLockedMessage={!canSelect && provider !== option}
          onSelect={onSelect}
          flex={1}
          minW="0"
        />
      ))}
    </InlineStack>
  );
}

/** Android sends only through Firebase, so there is nothing to choose. */
export function AndroidProviderCard() {
  return (
    <ProviderOptionCard
      provider="fcm"
      name="provider"
      isSelected={true}
      isDisabled={true}
      data-test-provider-option="fcm"
    />
  );
}
